import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

type ProgressGradientProps = Omit<ComponentProps<'div'>, 'role'> & {
  value: number;
  min?: number;
  max?: number;
  trackClassName?: string;
  indicatorClassName?: string;
};

export function ProgressGradient({
  value,
  min = 0,
  max = 100,
  className,
  trackClassName,
  indicatorClassName,
  ...props
}: ProgressGradientProps) {
  const safeMin = Math.min(min, max);
  const safeMax = Math.max(min, max);
  const clampedValue = Math.min(Math.max(value, safeMin), safeMax);
  const ratio =
    safeMax > safeMin ? (clampedValue - safeMin) / (safeMax - safeMin) : 0;
  const percentage = ratio * 100;
  const gradientBackgroundSize =
    percentage > 0 ? `${100 / ratio}% 100%` : '100% 100%';

  return (
    <div
      aria-valuemax={safeMax}
      aria-valuemin={safeMin}
      aria-valuenow={clampedValue}
      className={cn(
        'relative h-2 w-full overflow-hidden rounded-full border border-gray-300',
        trackClassName,
        className,
      )}
      role='progressbar'
      {...props}
    >
      <div
        className={cn(
          'h-full bg-linear-to-r from-orange-500 via-amber-400 to-green-500 transition-all',
          indicatorClassName,
        )}
        style={{
          width: `${percentage}%`,
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: gradientBackgroundSize,
        }}
      />
    </div>
  );
}
